import type { ObbTakeoffData } from '../data/obbTakeoff';

interface Props {
  data: ObbTakeoffData;
  onClose: () => void;
}

export function ObbTakeoffPanel({ data, onClose }: Props) {
  const orderQty = (item: ObbTakeoffData['items'][number]) => {
    const raw = item.modelQty * (1 + item.wastePercent / 100);
    return item.unit === 'ea' ? Math.ceil(raw) : Math.ceil(raw * 10) / 10;
  };

  const accentStyle = { color: data.color };
  const chipBg = `${data.color}1a`;
  const chipBorder = `${data.color}40`;

  return (
    <div className="w-[300px] rounded-xl bg-zinc-900/90 backdrop-blur-md overflow-hidden text-sm">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-white/10">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold text-white text-base leading-tight">{data.title}</div>
            <div className="text-white/45 text-xs mt-0.5">Kimbark Residence · {data.subtitle}</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors mt-0.5 flex-shrink-0">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Element chip */}
        <div
          className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2"
          style={{ background: chipBg, border: `1px solid ${chipBorder}` }}
        >
          <div className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: data.color, opacity: 0.85 }} />
          <div className="text-xs text-white/80 leading-tight">
            <span className="font-semibold" style={accentStyle}>{data.elementLabel}</span>
            <span className="text-white/45 ml-1">{data.elementDimensions}</span>
          </div>
        </div>
      </div>

      {/* Quantities table */}
      <div className="px-4 py-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/35">
          Measured quantities
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-white/35 text-[10px]">
              <th className="text-left pb-1.5 font-medium">Item</th>
              <th className="text-right pb-1.5 font-medium">Model</th>
              <th className="text-right pb-1.5 font-medium">Waste</th>
              <th className="text-right pb-1.5 font-medium">Order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.items.map(item => (
              <tr key={item.label}>
                <td className="py-2 text-white/80 pr-2">{item.label}</td>
                <td className="py-2 text-right text-white/60 tabular-nums">{item.modelQty} {item.unit}</td>
                <td className="py-2 text-right text-white/40 tabular-nums">{item.wastePercent}%</td>
                <td className="py-2 text-right font-semibold tabular-nums" style={accentStyle}>
                  {orderQty(item)} {item.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between">
        <span className="text-[10px] text-white/30">Scale verified · {data.scanLabel}</span>
        <button className="flex items-center gap-1.5 rounded-md bg-white/8 hover:bg-white/12 transition-colors px-2.5 py-1.5 text-[11px] text-white/60 hover:text-white">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
      </div>
    </div>
  );
}

export default ObbTakeoffPanel;
